import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Heart, CheckCircle2, ArrowRight, Loader2, AlertTriangle } from 'lucide-react';
import client from '../api/client';
import { useAuth } from '../contexts/AuthContext';

const TIER_NAMES = {
  sweetheart: 'Sweetheart',
  beloved: 'Beloved',
  concierge: 'AI Wedding Concierge',
  wedding_pass: 'Wedding Pass',
  extended_hosting: 'Extended Hosting',
};

// Polls /api/payments/status/{session_id} until the server confirms the
// webhook (or the fallback Stripe read) has flipped the transaction to
// paid. Falls back to a friendly retry state after 30 attempts so a bad
// network doesn't strand couples on the loading screen.
const POLL_INTERVAL_MS = 2000;
const POLL_MAX = 30;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState({ loading: true, paid: false, tier: null, error: null });

  useEffect(() => {
    if (!sessionId) {
      setStatus({ loading: false, paid: false, tier: null, error: 'No session id in URL.' });
      return;
    }
    let attempts = 0;
    let cancelled = false;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await client.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === 'paid') {
          setStatus({ loading: false, paid: true, tier: data.tier, error: null });
          if (refresh) refresh(); // pull fresh plan flag onto the user
          return;
        }
        if (attempts >= POLL_MAX) {
          setStatus({ loading: false, paid: false, tier: data.tier, error: 'Still waiting for confirmation from Stripe. Try refreshing in a moment.' });
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setStatus({ loading: false, paid: false, tier: null, error: e?.response?.data?.detail || e.message });
      }
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [sessionId, refresh]);

  const tierName = TIER_NAMES[status.tier] || 'Plan';

  return (
    <div className="min-h-screen bg-[#FBF7F2] flex flex-col items-center justify-center px-6 py-12">
      <Link to="/" className="flex items-center gap-2 mb-10">
        <div className="w-9 h-9 rounded-full bg-[#7B2C3B] flex items-center justify-center">
          <Heart className="w-4 h-4 text-[#FBF7F2] fill-[#FBF7F2]" />
        </div>
        <span className="font-serif text-2xl font-semibold">
          Yourwedding<span className="italic text-[#7B2C3B]">friends</span>
        </span>
      </Link>

      <div className="soft-card rounded-3xl p-10 max-w-lg text-center" data-testid="checkout-success-card">
        {status.loading && (
          <>
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#7B2C3B] mb-4" />
            <h1 className="font-serif text-3xl text-[#2A2A2A] mb-2">Confirming your payment…</h1>
            <p className="text-sm text-[#6A6A6A]">Hang tight for a few seconds while Stripe finishes up.</p>
          </>
        )}
        {!status.loading && status.paid && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-[#F4D6CE] flex items-center justify-center mb-5">
              <CheckCircle2 className="w-8 h-8 text-[#7B2C3B]" />
            </div>
            <p className="section-eyebrow">Welcome to {tierName}</p>
            <h1 className="font-serif text-4xl text-[#2A2A2A] mt-3 mb-3">
              You're all set, {user?.partner1 || 'friend'}.
            </h1>
            <p className="text-[#4A4A4A]">Everything is unlocked in your dashboard. Enjoy the calmer planning.</p>
            <button
              onClick={() => navigate('/dashboard')}
              data-testid="checkout-success-cta"
              className="mt-6 btn-primary w-full py-3 rounded-full text-sm font-medium inline-flex items-center justify-center gap-2"
            >
              Open my dashboard <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}
        {!status.loading && !status.paid && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-[#F4E1D1] flex items-center justify-center mb-5">
              <AlertTriangle className="w-8 h-8 text-[#7B2C3B]" />
            </div>
            <h1 className="font-serif text-3xl text-[#2A2A2A] mb-3">Not confirmed yet.</h1>
            <p className="text-sm text-[#6A6A6A] mb-5">{status.error || 'Your payment may still be processing. Give it a moment and refresh — or head to the dashboard.'}</p>
            <button
              onClick={() => window.location.reload()}
              data-testid="checkout-success-retry"
              className="btn-primary w-full py-3 rounded-full text-sm font-medium"
            >Refresh</button>
            <button onClick={() => navigate('/dashboard')} className="mt-2 w-full text-xs text-[#7B2C3B] hover:underline">Back to dashboard</button>
          </>
        )}
      </div>
    </div>
  );
}
